import React, { useState } from 'react';
import { Button } from '@heroui/react';
import Close from '@/icons/Close';
import Facebook from '@/icons/Facebook';
import Twitter from '@/icons/Twitter';
import Linkedin from '@/icons/Linkedin';
import Youtube from '@/icons/Youtube';
import Duplicate from '@/icons/Duplicate';
import Email from '@/icons/Email';

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    eventTitle: string;
    eventUrl: string;
    eventImage?: string;
    eventDescription?: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
    isOpen,
    onClose,
    eventTitle,
    eventUrl,
    eventImage,
    eventDescription
}) => {
    const [copied, setCopied] = useState(false);

    const shareData = {
        title: eventTitle,
        text: eventDescription || `Check out this amazing event: ${eventTitle}`,
        url: eventUrl,
    };

    // Native Web Share API
    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share(shareData);
                onClose();
            } catch (error) {
                console.log('Error sharing:', error);
            }
        } else {
            // Fallback to copy to clipboard
            handleCopyToClipboard();
        }
    };

    // Copy to clipboard
    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(eventUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.log('Error copying to clipboard:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = eventUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // Social media sharing
    const shareToSocial = (platform: string) => {
        const encodedUrl = encodeURIComponent(eventUrl);
        const encodedTitle = encodeURIComponent(eventTitle);
        const encodedDescription = encodeURIComponent(eventDescription || '');

        let shareUrl = '';

        switch (platform) {
            case 'facebook':
                // Facebook sharing with proper parameters
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`;
                break;
            case 'twitter':
                // Twitter sharing with better text formatting
                const twitterText = encodeURIComponent(`Check out this event: ${eventTitle}`);
                shareUrl = `https://twitter.com/intent/tweet?text=${twitterText}&url=${encodedUrl}`;
                break;
            case 'linkedin':
                // LinkedIn sharing with title and summary
                shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}`;
                break;
            case 'email':
                // Email sharing with better formatting
                const subject = encodeURIComponent(`Check out this event: ${eventTitle}`);
                const body = encodeURIComponent(`Hi there!

I thought you might be interested in this event:

${eventTitle}

${eventDescription || 'Check out this amazing event!'}

Event URL: ${eventUrl}

Best regards`);
                shareUrl = `mailto:?subject=${subject}&body=${body}`;
                break;
            case 'whatsapp':
                // WhatsApp sharing
                const whatsappText = encodeURIComponent(`Check out this event: ${eventTitle}\n\n${eventDescription || ''}\n\n${eventUrl}`);
                shareUrl = `https://wa.me/?text=${whatsappText}`;
                break;
            default:
                return;
        }

        // Open in popup window with proper dimensions
        const popup = window.open(shareUrl, '_blank', 'width=600,height=500,scrollbars=yes,resizable=yes');

        // Focus the popup if it was blocked
        if (popup) {
            popup.focus();
        } else {
            // Fallback: open in new tab if popup was blocked
            window.open(shareUrl, '_blank');
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-nightElevated rounded max-w-md w-full p-6 relative transition-colors"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-grey20 dark:text-nightTextSecondary">Share Event</h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded transition-colors"
                    >
                        <Close width={24} height={24} />
                    </button>
                </div>

                {/* Event Preview */}
                <div className="bg-gray-50 dark:bg-nightBg rounded p-4 mb-6">
                    <div className="flex items-center gap-3">
                        {eventImage && (
                            <img
                                src={eventImage}
                                alt={eventTitle}
                                className="w-16 h-16 rounded object-cover"
                            />
                        )}
                        <div className="flex-1 min-w-0">
                            <h3 className="font-vazirmatn font-semibold text-grey20 dark:text-nightTextSecondary text-sm line-clamp-2 mb-1">
                                {eventTitle}
                            </h3>
                            <p className="font-vazirmatn text-xs text-grey30 dark:text-nightMuted line-clamp-2">
                                {eventDescription}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Share Options */}
                <div className="space-y-4">
                    {/* Native Share / Copy Link */}
                    <Button
                        onClick={handleNativeShare}
                        className="w-full bg-primary50 text-white py-3 rounded font-semibold hover:bg-primary60 transition-colors dark:bg-nightAccent dark:text-[#080808] dark:hover:bg-nightAccent/90"
                    >
                        {typeof navigator !== 'undefined' && typeof navigator.share === 'function' ? 'Share' : 'Copy Link'}
                    </Button>



                    {/* Event URL with Copy Icon */}
                    <div className="bg-gray-50 dark:bg-nightBg rounded p-3">
                        <div className="text-xs text-grey30 dark:text-nightMuted mb-2 font-semibold">Event URL</div>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-grey20 dark:text-nightTextSecondary font-mono bg-white dark:bg-nightElevated px-2 py-1 rounded border dark:border-nightBorder truncate">
                                    {eventUrl}
                                </div>
                            </div>
                            <button
                                onClick={handleCopyToClipboard}
                                className="p-2 hover:bg-gray-200 dark:hover:bg-white/10 rounded transition-colors"
                                title="Copy URL"
                            >
                                <Duplicate width={16} height={16} color="#687879" />
                            </button>
                        </div>
                    </div>

                    {/* Social Media Buttons */}
                    <div className="grid grid-cols-5 gap-3">
                        <button
                            onClick={() => shareToSocial('facebook')}
                            className="flex flex-col items-center gap-2 p-3 rounded hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                        >
                            <div className="w-10 h-10 bg-blue-600 rounded flex items-center justify-center">
                                <Facebook width={20} height={20} color="#ffffff" />
                            </div>
                            <span className="text-xs text-grey30 dark:text-nightMuted">Facebook</span>
                        </button>

                        <button
                            onClick={() => shareToSocial('twitter')}
                            className="flex flex-col items-center gap-2 p-3 rounded hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                        >
                            <div className="w-10 h-10 bg-blue-400 rounded flex items-center justify-center">
                                <Twitter width={20} height={20} color="#ffffff" />
                            </div>
                            <span className="text-xs text-grey30 dark:text-nightMuted">Twitter</span>
                        </button>

                        <button
                            onClick={() => shareToSocial('linkedin')}
                            className="flex flex-col items-center gap-2 p-3 rounded hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                        >
                            <div className="w-10 h-10 bg-blue-700 rounded flex items-center justify-center">
                                <Linkedin width={20} height={20} color="#ffffff" />
                            </div>
                            <span className="text-xs text-grey30 dark:text-nightMuted">LinkedIn</span>
                        </button>

                        <button
                            onClick={() => shareToSocial('email')}
                            className="flex flex-col items-center gap-2 p-3 rounded hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                        >
                            <div className="w-10 h-10 bg-gray-600 rounded flex items-center justify-center">
                                <Email width={20} height={20} color="#ffffff" />
                            </div>
                            <span className="text-xs text-grey30 dark:text-nightMuted">Email</span>
                        </button>

                        <button
                            onClick={() => shareToSocial('whatsapp')}
                            className="flex flex-col items-center gap-2 p-3 rounded hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                        >
                            <div className="w-10 h-10 bg-green-500 rounded flex items-center justify-center">
                                <svg width="20" height="20" fill="#fff" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                                </svg>
                            </div>
                            <span className="text-xs text-grey30 dark:text-nightMuted">WhatsApp</span>
                        </button>
                    </div>

                    {/* Success Message */}
                    {copied && (
                        <div className="bg-green-50 dark:bg-nightSurface border border-green-200 dark:border-nightBorder rounded p-3 text-center">
                            <p className="text-green-800 dark:text-nightAccent text-sm font-medium">
                                Link copied to clipboard!
                            </p>
                        </div>
                    )}
                </div>
            </div>


        </div>
    );
};

export default ShareModal; 